import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { toast } from '@livable/saas-react-components';
import React, { useState } from 'react';
import { ConfirmActionModal } from 'src/pages/BillsPage/BillDetails/ConfirmActionModal';
import { useStore } from 'src/stores';
import { isOnUtility, refreshArchivedAllocation } from './archivedAllocationRefresh';

/**
 * What removing the allocation does, for the confirmation. Archived, never wiped: its charges come off and the table is
 * pending again. An uploaded bill it was made from stays on file, no longer allocated, so it can be allocated again --
 * from the Bills page, since the table lets go of it to take another (LB-6028).
 */
export function getAllocationRemovalConfirmation() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    _ref$isMadeFromBill = _ref.isMadeFromBill,
    isMadeFromBill = _ref$isMadeFromBill === void 0 ? false : _ref$isMadeFromBill;
  const archived = 'This allocation will be archived. Its charges come off this table, which goes back to pending.';
  return isMadeFromBill ? "".concat(archived, " The bill it was made from stays on the Bills page, ready to allocate again, and this table is free ") + 'for another bill.' : "".concat(archived, " Enter the bill again to allocate it.");
}

/**
 * Why a removal did not happen, from what property-service answered (LB-5953). It refuses an allocation already
 * billed to residents with a 400, and answers 404 for one that is no longer there -- removed, or archived with its
 * bill, since the screen loaded.
 */
export function getAllocationRemovalRefusal(status) {
  if (status === 400) {
    return 'This allocation has been billed to residents, or its billing month has closed, so it can no longer be removed.';
  }
  if (status === 404) return 'This allocation is no longer there. It may have been removed already.';
  return 'We couldn’t remove this allocation. Try again.';
}

/**
 * Removing an allocation from apply billing (LB-5953), with its confirmation: for any allocation, typed in by hand or
 * made from an uploaded bill, until it is billed. The way to redo one -- an allocation made from a bill otherwise only
 * lets go of it by that bill being deleted.
 *
 * Property-service archives it. The screen reloads it, if still on it, without the bill the allocation was made from
 * (LB-6028): that bill stays on file for the Bills page, and the table takes another in its place -- uploaded, chosen
 * from earlier bills or typed in. Held instead, it left the table no way to take one: a bill on screen hides the upload
 * area, and one this screen did not upload cannot be deleted from it.
 *
 * @param property the property the modal is open on
 * @returns `request(bill)` to ask for the removal of that allocation bill, and the confirmation to render
 */
export function useAllocationRemove() {
  let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    property = _ref2.property;
  const store = useStore();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    asked = _useState2[0],
    setAsked = _useState2[1];
  const request = bill => {
    var _store$homeDashboardS;
    if (!(bill !== null && bill !== void 0 && bill.id)) return;

    // What the screen was on when the removal was asked for, so a late answer only refreshes that.
    const applyBill = ((_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 || (_store$homeDashboardS = _store$homeDashboardS.billingTab) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.applyBill) || {};
    setAsked({
      billId: bill.id,
      from: {
        billingCycleId: applyBill.billingCycleId,
        propertyId: property === null || property === void 0 ? void 0 : property.id,
        utilityId: applyBill.utilityId
      },
      sourceBillId: bill.sourceBillId || null
    });
  };

  /**
   * Lets go of the bill the removed allocation was made from, so the upload area takes its place. The screen starts this
   * table over -- no bill held, no file copy of one uploaded since the modal opened, no residents' tick, which the next
   * bill gets only when someone chooses it -- and keeps only which allocation it removed. The lookup does not fetch that
   * allocation's bill back (allocationBillLookup): not through the allocation, still in the store until the reload lands,
   * nor as the cycle's bill, which until bill-automation passes over it is this very one. A table allocated again
   * meanwhile, from another bill or this same one, is a different allocation, and its bill is fetched whenever its load
   * lands. Only while the screen is still on the utility the removal was asked from: the held state is shared by every
   * utility.
   *
   * @param resetCountWhenConfirmed how many times the screen had started over when the removal was confirmed. Started over
   *   since -- the table closed and opened again while a slow removal was out -- what it holds now is newer than the
   *   removal, whatever bill or file that is, and is not undone by it.
   */
  const releaseSourceBill = (_ref3, resetCountWhenConfirmed) => {
    var _store$homeDashboardS2, _allocations$tempFile;
    let billId = _ref3.billId,
      sourceBillId = _ref3.sourceBillId,
      from = _ref3.from;
    const allocations = property === null || property === void 0 ? void 0 : property.allocations;
    if (!sourceBillId || !allocations) return;
    if (!isOnUtility((_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.billingTab, from)) return;
    if (((_allocations$tempFile = allocations.tempFileData) === null || _allocations$tempFile === void 0 ? void 0 : _allocations$tempFile.resetCount) === resetCountWhenConfirmed) allocations.resetTempFileData();
    allocations.setTempFileData('releasedAllocationBillId', billId);
  };
  const confirm = async () => {
    var _property$allocations;
    const removing = asked;
    setAsked(null);
    if (!removing) return;
    const resetCountWhenConfirmed = property === null || property === void 0 || (_property$allocations = property.allocations) === null || _property$allocations === void 0 || (_property$allocations = _property$allocations.tempFileData) === null || _property$allocations === void 0 ? void 0 : _property$allocations.resetCount;
    const remove = store.allocations.removeBillAsync;
    const resp = await remove.run({
      billId: removing.billId
    });
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) {
      var _remove$error;
      const status = (_remove$error = remove.error) === null || _remove$error === void 0 ? void 0 : _remove$error.status;
      toast('error', {
        title: 'Allocation not removed',
        body: [getAllocationRemovalRefusal(status)]
      });
      // Gone already -- removed elsewhere, or archived with its bill: what is there now is shown instead, letting go of
      // its bill as a removal here does.
      if (status === 404) {
        releaseSourceBill(removing, resetCountWhenConfirmed);
        await refreshArchivedAllocation({
          store,
          property,
          from: removing.from
        });
      }
      return;
    }
    toast('success', {
      body: ['Allocation removed and archived.']
    });
    releaseSourceBill(removing, resetCountWhenConfirmed);
    await refreshArchivedAllocation({
      store,
      property,
      from: removing.from
    });
  };
  const confirmation = /*#__PURE__*/React.createElement(ConfirmActionModal, {
    confirmLabel: "Remove allocation",
    isOpen: !!asked,
    message: getAllocationRemovalConfirmation({
      isMadeFromBill: !!(asked !== null && asked !== void 0 && asked.sourceBillId)
    }),
    onClose: () => setAsked(null),
    onConfirm: confirm,
    title: "Remove allocation?"
  });
  return {
    request,
    confirmation
  };
}